import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { useVisitorStore } from '@/stores/visitorStore';

export default function Contact() {
  const visitorFingerprint = useVisitorStore((s) => s.visitorFingerprint);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    isBusinessOwner: false,
    companyWebsite: '',
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient<{ id: string; status: string }>('/public/contact', {
        method: 'POST',
        body,
      }),
    onSuccess: () => setSubmitted(true),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || undefined,
      message: form.message.trim(),
      is_business_owner: form.isBusinessOwner,
      company_website: form.companyWebsite.trim() || undefined,
      visitor_fingerprint: visitorFingerprint || undefined,
    });
  };

  if (submitted) {
    return (
      <div className="container-page py-16">
        <div className="mx-auto max-w-lg rounded-brand border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Message sent</h1>
          <p className="mt-2 text-slate-600">
            Thank you for reaching out. Our team will get back to you within 1–2 business days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-page py-10">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-3xl font-bold text-slate-900">Contact us</h1>
        <p className="mt-2 text-slate-600">
          Have questions about listing your business or need support? We&apos;re here to help.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-6 rounded-brand border border-slate-200 bg-white p-8 shadow-sm">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.isBusinessOwner}
              onChange={(e) => setForm({ ...form, isBusinessOwner: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-primary focus:ring-brand-primary"
            />
            <span className="text-sm text-slate-700">I want to list my business on QRBusiness India</span>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                Name *
              </label>
              <input
                id="name"
                type="text"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
                Phone *
              </label>
              <input
                id="phone"
                type="tel"
                required
                minLength={10}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>
          </div>

          <div className="absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="company_website">Company website</label>
            <input
              id="company_website"
              type="text"
              name="company_website"
              tabIndex={-1}
              autoComplete="off"
              value={form.companyWebsite}
              onChange={(e) => setForm({ ...form, companyWebsite: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-700">
              Message *
            </label>
            <textarea
              id="message"
              required
              minLength={3}
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us how we can help..."
              className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600" role="alert">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Could not send message. Please try again.'}
            </p>
          )}

          <Button type="submit" size="lg" disabled={mutation.isPending}>
            {mutation.isPending ? 'Sending…' : 'Send message'}
          </Button>
        </form>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <div className="rounded-brand bg-slate-100 p-6">
            <h2 className="font-semibold text-slate-900">For business owners</h2>
            <p className="mt-2 text-sm text-slate-600">
              Get a free digital storefront, QR code, and analytics dashboard for your shop.
            </p>
          </div>
          <div className="rounded-brand bg-slate-100 p-6">
            <h2 className="font-semibold text-slate-900">Support</h2>
            <p className="mt-2 text-sm text-slate-600">
              Email us at{' '}
              <a href="mailto:support@qrbusiness.in" className="text-brand-primary hover:underline">
                support@qrbusiness.in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
